'use client';

/**
 * Second step of a login that hit the 2FA challenge. Six surfaces mount it
 * (sign-in, Whop / Discord callbacks, choose-plan checkout + OTP, courses
 * gate); each passes the challenge it received and its own post-session
 * handler. The challenge token lives only in the parent's React state —
 * never a cookie, never storage, never a log line.
 */

import { FlowCta } from '@/components/auth/flow-cta';
import { StatusLine, type StatusLineState } from '@/components/auth/status-line';
import { TotpCodeInput, type TotpStatus } from '@/components/auth/totp-code-input';
import { UnderlineField } from '@/components/auth/underline-field';
import { AnalyticsEvent } from '@/shared/lib/analytics/events';
import { describeTwoFactorError } from '@/shared/lib/auth/two-factor-errors';
import { useAuthStore } from '@/shared/stores/auth-store';
import { cn } from '@/shared/utils/formatting';
import type { AuthResponse } from '@/types';
import { ArrowLeft, ArrowRight, KeyRound, Smartphone } from 'lucide-react';
import posthog from 'posthog-js';
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

/** The entry that was challenged — mirrors the `method` on `auth_login_completed`. */
export type TwoFactorMethod =
  | 'email'
  | 'otp'
  | 'google'
  | 'apple'
  | 'whop'
  | 'discord'
  | 'discord_link'
  | 'checkout'
  | 'course_otp'
  | 'course_checkout';

type Factor = 'totp' | 'recovery';

export interface TwoFactorChallengeProps {
  challengeToken: string;
  method: TwoFactorMethod;
  /** Runs after the session is applied to the store; do the surface's own navigation here. */
  onSuccess: (response: AuthResponse) => void;
  /** The challenge is dead (stale, replayed) or the user backed out — return to credentials. */
  onRestart: () => void;
  /** Shown in the intro copy when the surface knows it. */
  email?: string;
  rememberMe?: boolean;
  className?: string;
}

const RECOVERY_LENGTH = 11;
const VERIFIED_HOLD_MS = 500;

const TOTP_STATUS: Record<StatusLineState, TotpStatus> = {
  idle: '',
  checking: '',
  ok: 'ok',
  err: 'err',
};

function lineStateFor(
  verified: boolean,
  error: string | null,
  submitting: boolean
): StatusLineState {
  if (verified) return 'ok';
  if (error) return 'err';
  if (submitting) return 'checking';
  return 'idle';
}

/** `abcde fghij` → `ABCDE-FGHIJ`; tolerant of pasted hyphens and spaces. */
export function formatRecoveryCode(raw: string): string {
  const clean = raw
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '')
    .slice(0, RECOVERY_LENGTH - 1);
  return clean.length > 5 ? `${clean.slice(0, 5)}-${clean.slice(5)}` : clean;
}

function recoveryUsedCopy(remaining: number | undefined): string {
  const left = typeof remaining === 'number' ? ` You have ${remaining} left.` : '';
  return `You signed in with a recovery code.${left} Consider generating new ones.`;
}

export function TwoFactorChallenge({
  challengeToken,
  method,
  onSuccess,
  onRestart,
  email,
  rememberMe,
  className,
}: TwoFactorChallengeProps) {
  const [factor, setFactor] = useState<Factor>('totp');
  const [totp, setTotp] = useState('');
  const [recovery, setRecovery] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [verified, setVerified] = useState(false);
  const [retryAfter, setRetryAfter] = useState(0);
  const lockRef = useRef(false);

  useEffect(() => {
    posthog.capture(AnalyticsEvent.AUTH_2FA_CHALLENGE_SHOWN, { method });
  }, [method]);

  useEffect(() => {
    if (retryAfter <= 0) return;
    const t = setTimeout(() => setRetryAfter((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [retryAfter]);

  useEffect(() => {
    if (submitting) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onRestart();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onRestart, submitting]);

  const submit = useCallback(
    async (input: { code: string } | { recoveryCode: string }) => {
      if (lockRef.current) return;
      lockRef.current = true;
      setSubmitting(true);
      setError(null);
      const usedFactor: Factor = 'code' in input ? 'totp' : 'recovery';
      try {
        const response = await useAuthStore.getState().completeTwoFactor({
          challengeToken,
          ...input,
          ...(rememberMe !== undefined ? { rememberMe } : {}),
        });
        posthog.capture(
          AnalyticsEvent.AUTH_2FA_VERIFIED,
          { method, factor: usedFactor },
          { send_instantly: true }
        );
        if (response.recovery_used) {
          toast.warning(recoveryUsedCopy(response.user.recovery_codes_remaining), {
            duration: 10_000,
          });
        }
        setVerified(true);
        // Let the green "Verified" state land before the surface navigates.
        await new Promise((resolve) => setTimeout(resolve, VERIFIED_HOLD_MS));
        onSuccess(response);
      } catch (err) {
        const info = describeTwoFactorError(err);
        posthog.capture(AnalyticsEvent.AUTH_2FA_VERIFY_FAILED, {
          method,
          factor: usedFactor,
          error_code: info.errorCode,
          ...(info.httpStatus !== undefined ? { http_status: info.httpStatus } : {}),
        });
        if (info.kind === 'restart') {
          toast.error(info.message);
          onRestart();
          return;
        }
        if (info.kind === 'throttled') setRetryAfter(info.retryAfterSeconds ?? 60);
        setError(info.message);
        setTotp('');
      } finally {
        lockRef.current = false;
        setSubmitting(false);
      }
    },
    [challengeToken, method, onRestart, onSuccess, rememberMe]
  );

  const disabled = submitting || verified || retryAfter > 0;
  const recoveryReady = recovery.length === RECOVERY_LENGTH;
  const lineState = lineStateFor(verified, error, submitting);
  const retrySuffix = retryAfter > 0 ? ` (${retryAfter}s)` : '';
  const fieldError = error ? `${error}${retrySuffix}` : undefined;

  const switchFactor = () => {
    setFactor((f) => (f === 'totp' ? 'recovery' : 'totp'));
    setError(null);
    setTotp('');
    setRecovery('');
  };

  const linkBtn =
    'inline-flex items-center gap-1.5 text-[13px] font-medium transition-colors disabled:opacity-70 [&_svg]:h-[13px] [&_svg]:w-[13px]';

  return (
    <div className={cn('flex w-full flex-col gap-[30px] ph-no-capture', className)}>
      <div className="flex flex-col gap-2">
        <span className="inline-flex items-center gap-2 text-[10.5px] font-semibold uppercase tracking-[0.18em] text-red-primary before:block before:h-px before:w-[18px] before:bg-red-primary before:content-['']">
          Two-factor authentication
        </span>
        <h2 className="mt-1.5 text-[32px] font-extrabold leading-[1.05] tracking-[-0.03em] text-text-primary sm:text-[38px]">
          {factor === 'totp' ? 'Check your phone' : 'Use a recovery code'}
        </h2>
        <p className="text-[15px] leading-[1.5] text-text-secondary">
          {factor === 'totp' ? (
            <>
              Enter the 6-digit code from your authenticator app
              {email ? (
                <>
                  {' '}
                  for <b className="font-semibold text-text-primary">{email}</b>
                </>
              ) : null}
              .
            </>
          ) : (
            'Enter one of the 8 codes you saved when you turned on two-factor. It works once.'
          )}
        </p>
      </div>

      {factor === 'totp' ? (
        <div className="flex flex-col gap-4 animate-[step-in_0.38s_cubic-bezier(0.2,0.9,0.2,1)_both]">
          <TotpCodeInput
            size="lg"
            value={totp}
            onChange={(value) => {
              setTotp(value);
              if (error) setError(null);
            }}
            onComplete={(value) => void submit({ code: value })}
            disabled={disabled}
            status={TOTP_STATUS[lineState]}
            autoFocus
          />
          <StatusLine
            state={lineState}
            idle="Signs you in automatically when all six are in."
            okLabel="Verified"
            error={fieldError}
          />
        </div>
      ) : (
        <form
          method="post"
          className="flex flex-col gap-6 animate-[step-in_0.38s_cubic-bezier(0.2,0.9,0.2,1)_both]"
          onSubmit={(e) => {
            e.preventDefault();
            if (recoveryReady && !disabled) void submit({ recoveryCode: recovery });
          }}
        >
          <UnderlineField
            label="Recovery code"
            icon="key"
            mono
            value={recovery}
            onChange={(e) => {
              setRecovery(formatRecoveryCode(e.target.value));
              if (error) setError(null);
            }}
            placeholder="XXXXX-XXXXX"
            autoComplete="one-time-code"
            autoCapitalize="characters"
            spellCheck={false}
            maxLength={RECOVERY_LENGTH}
            disabled={disabled}
            autoFocus
            error={fieldError}
            hint="One of the 8 codes you saved when you turned on two-factor. Each works once."
          />
          <FlowCta type="submit" disabled={!recoveryReady || disabled} loading={submitting}>
            Sign in
            <ArrowRight aria-hidden />
          </FlowCta>
        </form>
      )}

      <div className="flex flex-col items-start gap-[18px]">
        <button
          type="button"
          onClick={switchFactor}
          disabled={submitting}
          className={cn(linkBtn, 'text-text-tertiary hover:text-text-primary')}
        >
          {factor === 'totp' ? (
            <>
              <KeyRound aria-hidden />
              Use a recovery code instead
            </>
          ) : (
            <>
              <Smartphone aria-hidden />
              Use my authenticator app instead
            </>
          )}
        </button>
        <button
          type="button"
          onClick={onRestart}
          disabled={submitting}
          className={cn(linkBtn, 'text-red-primary hover:text-red-hover')}
        >
          <ArrowLeft aria-hidden />
          Back to sign in
        </button>
      </div>
    </div>
  );
}
